import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGestaoUsuarios } from '../context/GestaoUsuariosProvider';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  UserX, 
  UserCheck,
  Building,
  Download
} from 'lucide-react';
import * as XLSX from 'xlsx';

const ListaColaboradores = () => {
  const { 
    colaboradores, 
    setores,
    loading, 
    filtrosColaboradores, 
    setFiltrosColaboradores, 
    carregarColaboradores,
    carregarSetores,
    toggleStatusColaborador 
  } = useGestaoUsuarios();

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    carregarColaboradores();
    carregarSetores();
  }, [carregarColaboradores, carregarSetores]);

  // Aplicar filtros
  const colaboradoresFiltrados = colaboradores.filter(colaborador => {
    // Filtro por status
    if (filtrosColaboradores.status === 'ativo' && !colaborador.ativo) return false;
    if (filtrosColaboradores.status === 'inativo' && colaborador.ativo) return false;
    
    // Filtro por setor
    if (filtrosColaboradores.setor && colaborador.setor !== parseInt(filtrosColaboradores.setor)) return false;
    
    // Filtro por busca
    if (filtrosColaboradores.busca) {
      const busca = filtrosColaboradores.busca.toLowerCase();
      return (
        colaborador.nome.toLowerCase().includes(busca) ||
        colaborador.email?.toLowerCase().includes(busca) ||
        colaborador.celular?.includes(busca)
      );
    }
    
    return true;
  });

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltrosColaboradores(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const limparFiltros = () => {
    setFiltrosColaboradores({
      status: '',
      setor: '',
      busca: ''
    });
  };

  const handleToggleStatus = async (id, ativo) => {
    if (window.confirm(`Tem certeza que deseja ${ativo ? 'ativar' : 'desativar'} este colaborador?`)) {
      await toggleStatusColaborador(id, ativo);
    }
  };

  const getSetorNome = (setorId) => {
    const setor = setores.find(s => s.id === setorId);
    return setor ? setor.descricao : 'Não definido';
  };

  const exportarParaExcel = () => {
    // Preparar dados para exportação
    const dadosExportacao = colaboradoresFiltrados.map(colaborador => ({
      'Nome': colaborador.nome,
      'Email': colaborador.email || '',
      'Telefone': colaborador.celular || '',
      'Setor': getSetorNome(colaborador.setor),
      'Status': colaborador.ativo ? 'Ativo' : 'Inativo',
      'Data de Cadastro': new Date(colaborador.created_at).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }));

    // Criar workbook e worksheet
    const ws = XLSX.utils.json_to_sheet(dadosExportacao);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Colaboradores');

    // Download do arquivo
    const dataAtual = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `colaboradores_${dataAtual}.xlsx`);
  };

  if (loading) {
    return (
      <div className="gestao-usuarios lista-full-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Carregando colaboradores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gestao-usuarios lista-full-container">
      <div className="lista-header">
        <div className="header-content">
          <h1>
            <Building size={32} />
            Gestão de Colaboradores
          </h1>
          <p>Gerencie colaboradores da organização (sem acesso ao sistema)</p>
        </div>
        <div className="header-actions">
          <button 
            onClick={exportarParaExcel}
            className="button secondary"
            disabled={colaboradoresFiltrados.length === 0}
          >
            <Download size={16} />
            Exportar Excel
          </button>
          <Link to="/gestao-usuarios/colaboradores/cadastro" className="button primary">
            <Plus size={16} />
            Novo Colaborador
          </Link>
        </div>
      </div>

      <div className="lista-content-wrapper">
        {/* Barra de busca e filtros */}
        <div className="search-filter-bar">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Buscar por nome, email ou telefone..."
              value={filtrosColaboradores.busca}
              onChange={(e) => setFiltrosColaboradores(prev => ({ ...prev, busca: e.target.value }))}
            />
          </div>
          <button 
            className="filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filtros
          </button>
        </div>

        {/* Painel de filtros */}
        {showFilters && (
          <div className="filters-panel">
            <div className="filters-grid">
              <div className="filter-group">
                <label>Status</label>
                <select name="status" value={filtrosColaboradores.status} onChange={handleFiltroChange}>
                  <option value="">Todos</option>
                  <option value="ativo">Ativos</option>
                  <option value="inativo">Inativos</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>Setor</label>
                <select name="setor" value={filtrosColaboradores.setor} onChange={handleFiltroChange}>
                  <option value="">Todos</option>
                  {setores.map(setor => (
                    <option key={setor.id} value={setor.id}>{setor.descricao}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="filter-actions">
              <button className="button secondary" onClick={limparFiltros}>
                Limpar Filtros
              </button>
            </div>
          </div>
        )}

        {/* Estatísticas rápidas */}
        <div className="quick-stats">
          <div className="stat-item">
            <span className="stat-label">Total:</span>
            <span className="stat-value">{colaboradoresFiltrados.length}</span>
          </div>
          <div className="stat-item ativo">
            <span className="stat-label">Ativos:</span>
            <span className="stat-value">{colaboradoresFiltrados.filter(c => c.ativo).length}</span>
          </div>
          <div className="stat-item inativo">
            <span className="stat-label">Inativos:</span>
            <span className="stat-value">{colaboradoresFiltrados.filter(c => !c.ativo).length}</span>
          </div>
        </div>

        {/* Lista de colaboradores */}
        {colaboradoresFiltrados.length === 0 ? (
          <div className="empty-state">
            <Building size={48} />
            <h3>Nenhum colaborador encontrado</h3>
            <p>Não há colaboradores que correspondam aos filtros aplicados.</p>
            <Link to="/gestao-usuarios/colaboradores/cadastro" className="button primary">
              <Plus size={16} />
              Cadastrar Primeiro Colaborador
            </Link>
          </div>
        ) : (
          <div className="usuarios-list-vertical">
            {colaboradoresFiltrados.map(colaborador => (
              <div key={colaborador.id} className="usuario-card-horizontal">
                <div className="card-main-content">
                  <div className="card-header-section">
                    <div className="usuario-info">
                      <h3 className="usuario-title">{colaborador.nome}</h3>
                      <div className="usuario-meta">
                        <span className="email">{colaborador.email}</span>
                        {colaborador.celular && (
                          <span className="telefone"> • {colaborador.celular}</span>
                        )}
                      </div>
                    </div>
                    <div className="status-badges">
                      <span className={`status-badge ${colaborador.ativo ? 'status-ativo' : 'status-inativo'}`}>
                        {colaborador.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                      <span className="nivel-badge nivel-colaborador">
                        <Building size={16} />
                        {getSetorNome(colaborador.setor)}
                      </span>
                    </div>
                  </div>

                  <div className="card-body-section">
                    <div className="info-line">
                      <span className="info-label">Cadastrado:</span>
                      <span className="info-value data-criacao">
                        {new Date(colaborador.created_at).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="card-actions-vertical">
                  <Link 
                    to={`/gestao-usuarios/colaboradores/detalhes/${colaborador.id}`}
                    className="action-button-full view"
                  >
                    <Eye size={16} />
                    Ver Detalhes
                  </Link>
                  
                  <Link 
                    to={`/gestao-usuarios/colaboradores/editar/${colaborador.id}`}
                    className="action-button-full edit"
                  >
                    <Edit size={16} />
                    Editar
                  </Link>
                  
                  <button
                    onClick={() => handleToggleStatus(colaborador.id, !colaborador.ativo)}
                    className={`action-button-full ${colaborador.ativo ? 'deactivate' : 'activate'}`}
                  >
                    {colaborador.ativo ? (
                      <>
                        <UserX size={16} />
                        Desativar
                      </>
                    ) : (
                      <>
                        <UserCheck size={16} />
                        Ativar
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListaColaboradores;